import { useEffect } from "react";
import { styled } from "styled-components";

import { Link, StyledModalText, UIButton } from "common/components";
import { LinkButton } from "common/components/button/LinkButton";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTranslate } from "common/utilities/useTranslate";
import { acceptMapShareInvite } from "domains/createShareSettings/actions";
import { interpolate } from "domains/language/interpolate";
import { deactivateModal } from "domains/modal/actions";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import { getModalParams } from "domains/modal/selectors";
import type { ModalParams } from "domains/modal/types";
import { removeNotification, updateNotification } from "domains/notifications/actions";
import { getNotification } from "domains/notifications/selectors";
import type { NotificationType } from "domains/notifications/types";

const StyledMessage = styled(StyledModalText)`
  margin: 0 0 1rem 0;
`;

const StyledButton = styled(UIButton)`
  margin-bottom: 1rem;
`;

const hasExpired = (expirationDate?: number) =>
  expirationDate ? new Date().getTime() > new Date(expirationDate * 1000).getTime() : false;

type ActionItem = { href?: string; key: string; onClick?: () => void; title: string; to?: string };

const supportedEntityTypes: NotificationType[] = ["topic", "event", "knowledge", "cluster"];

export const NotificationDetailModal = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const { id } = useSelector(getModalParams) as ModalParams<"NotificationDetail">;
  const notification = useSelector(getNotification(id));
  const translate = useTranslate();

  const notificationId = notification ? notification.id : undefined;

  useEffect(() => {
    if (notificationId) {
      dispatch(updateNotification({ id: notificationId, has_been_opened: true }));
    }
  }, [dispatch, notificationId]);

  if (!notification) {
    return null;
  }
  const { context, inviteExpired, isUpdatePending, mapShareInviteError, message, title, topicDeleted, type } =
    notification;

  const removeNotificationHandler = () => {
    if (id) {
      dispatch(removeNotification(id));
    }
  };

  const mapShareAction = () => {
    const { has_accepted, invite_token, topic } = context;
    if (has_accepted) {
      go(`/topics/${topic}`);
      dispatch(deactivateModal());
    } else if (invite_token && id) {
      dispatch(acceptMapShareInvite({ token: invite_token, id }));
    }
  };

  const closeModal = () => dispatch(deactivateModal());

  const getEntityHref = (type: NotificationType, id?: string) => {
    if (id === undefined) return undefined;

    switch (type) {
      case "topic":
        return `/topics/${id}`;
      case "event":
        return `/membership-events/${id}`;
      case "cluster":
        return `/monitor/emerging-themes/${id}`;
      case "knowledge":
        return `/monitor/latest-knowledge/${id}`;
      default:
        return undefined;
    }
  };

  const actions: ActionItem[] = [];
  const tokenHasExpired = inviteExpired === true || hasExpired(context?.expiration_date);
  if (type === "map_share" && context && !tokenHasExpired && !topicDeleted)
    actions.push({
      key: "map_share_action",
      onClick: mapShareAction,
      title: context.has_accepted
        ? translate("app.topic.view_transformation_map")
        : translate("app.notifications.map_invite.accept_invitation"),
    });
  if (context?.link)
    actions.push({ href: context.link, key: "map_open_link", title: translate("app.topic.actions.open") });
  if (supportedEntityTypes.includes(type) && context.entity_id)
    actions.push({
      key: `${type}action`,
      onClick: closeModal,
      title: translate("app.topic.actions.open"),
      to: getEntityHref(type, context.entity_id),
    });
  const supportEmail = (email: string) => <Link href={`mailto:${email}`}>{email}</Link>;

  let errorMessage = undefined;
  if (tokenHasExpired) errorMessage = translate("app.notifications.map_invite.expired_token");
  else if (topicDeleted) errorMessage = translate("app.notifications.map_invite.topic_deleted");
  else if (mapShareInviteError)
    errorMessage = interpolate(translate("app.topic.follow.error_message"), { supportEmail });

  return (
    <>
      <ModalHeader title={title || translate("app.notifications.title")} />
      <StyledMessage>{errorMessage ?? message}</StyledMessage>
      {actions.map((item) => (
        <LinkButton href={item.href} to={item.to} key={item.key}>
          <StyledButton onClick={item.onClick} isLoading={isUpdatePending} variant="filled" fullwidth>
            {item.title}
          </StyledButton>
        </LinkButton>
      ))}
      <UIButton variant="outlined" onClick={removeNotificationHandler} fullwidth>
        {translate("app.notification.delete_notification")}
      </UIButton>
    </>
  );
};
